import { parallelArray } from "@impero-com/common/utils/form/formAtom/validation";
import { toSplitAtomValidationError } from "@impero-com/common/utils/form/splitFormAtom";
import { TFunction } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import memoizeOne from "memoize-one";
import * as Yup from "yup";

import { DrawerBehavior } from "components/fieldBuilder/fieldDrawer";
import { Field } from "components/fieldBuilder/models";
import {
    FieldTypeInfo,
    findDependency,
    getCommonFieldProperties,
    getCommonFieldTypeInfo,
    getCommonFieldSchemaProperties,
    getCommonFieldTypeDefaultValue,
    getCommonFieldTypeSchema,
    getCommonFieldTypeDrawerBehavior,
    getCommonFieldTypeConditionalValues,
    getOptionSchema,
    renderPlaceholder,
    CommonFieldTypeName,
} from "components/fieldBuilder/utils";
import radioButtonSvg from "img/field/radiobutton.svg";

import {
    ConclusionFieldType,
    ControlConclusionFieldTypeName,
    handleDuplicateEffectivenessOptions,
    ControlConclusionField,
    SampleConclusionField,
    ControlConclusionFieldType,
    ConclusionFieldTypeName,
} from "./schema";

export function useFieldButtons(fieldTypeNames: CommonFieldTypeName[]) {
    const fieldButtons = fieldTypeNames.map((name) => {
        const info = getCommonFieldTypeInfo();
        return {
            name,
            label: info[name].label,
            svg: info[name].svg,
        };
    });
    return fieldButtons.map((fieldButton) => {
        return {
            id: fieldButton.name,
            icon: {
                svg: fieldButton.svg,
            },
            label: fieldButton.label,
            renderPlaceholder: () =>
                renderPlaceholder({
                    label: fieldButton.label,
                    svg: fieldButton.svg,
                }),
        };
    });
}

export const getConclusionFieldTypeInfo = memoizeOne((t: TFunction) => {
    const fieldTypeInfo: Record<ControlConclusionFieldTypeName, FieldTypeInfo> =
        {
            Effectiveness: {
                label: t("TestingControlView_TaskType_Effectiveness"),
                svg: radioButtonSvg,
            },
            ...getCommonFieldTypeInfo(),
        };

    return (fieldTypeName: ControlConclusionFieldTypeName): FieldTypeInfo => {
        const info = fieldTypeInfo[fieldTypeName];
        if (!info) {
            throw new Error("Unknown type of field " + fieldTypeName);
        }
        return info;
    };
});

export function getConclusionFieldTypeDrawerBehavior(
    fieldType: ConclusionFieldType
): DrawerBehavior {
    if (fieldType.type === "Effectiveness") {
        return {
            hideRequired: true,
            cannotDelete: true,
            cannotCopy: true,
            hideCondition: true,
        };
    }
    return getCommonFieldTypeDrawerBehavior(fieldType);
}

export function getConclusionFieldTypeConditionalValues(
    fieldType: ConclusionFieldType
) {
    if (fieldType.type === "Effectiveness") {
        return [
            fieldType.options.effectiveOptionName,
            fieldType.options.partiallyEffectiveOptionName,
            fieldType.options.notEffectiveOptionName,
        ];
    }
    return getCommonFieldTypeConditionalValues(fieldType);
}

function getFieldsTest(t: TFunction) {
    return {
        name: "condition",
        test: (
            values: Field<ConclusionFieldType, string>[],
            { createError }: Yup.TestContext<Yup.AnyObject>
        ) => {
            const PATH = "condition";
            const errorIndicesToMessages: Record<number, string> = {};
            values.forEach((value, index) => {
                const condition = value.condition;
                if (!condition) {
                    return;
                }
                const dependency = findDependency(
                    condition.fieldId,
                    values,
                    value.id
                );
                switch (dependency.type) {
                    case "FOUND": {
                        break;
                    }
                    case "FOUND_AFTER": {
                        errorIndicesToMessages[index] = t(
                            "FieldBuilder_Error_Condition_Dependency_FoundAfter"
                        );
                        return;
                    }
                    case "NOT_FOUND": {
                        errorIndicesToMessages[index] = t(
                            "FieldBuilder_Error_Condition_Dependency_NotFound"
                        );
                        return;
                    }
                    default: {
                        assertNever(dependency);
                    }
                }

                if (
                    !(
                        getConclusionFieldTypeConditionalValues(
                            dependency.value.fieldType
                        ).findIndex((v: string) => v === condition.value) >= 0
                    )
                ) {
                    errorIndicesToMessages[index] = t(
                        "FieldBuilder_Error_Condition_Dependency_ValueNotFound"
                    );
                }
            });

            if (Object.keys(errorIndicesToMessages).length > 0) {
                return toSplitAtomValidationError(
                    createError({
                        path: PATH,
                    }),
                    errorIndicesToMessages
                );
            }

            return true;
        },
    };
}

// NOTE: This is an adaptation of the original getFieldSchema intended for controls.
// Once the FieldBuilder is fully ported as a form agnostic component, only top level Zod schemas should be used
export function getControlConclusionFieldSchema(
    t: TFunction
): Yup.Schema<Field<ConclusionFieldType, string>[]> {
    return parallelArray()
        .of(
            Yup.object({
                fieldType: Yup.lazy(
                    (
                        fieldType: ConclusionFieldType
                    ): Yup.ISchema<ConclusionFieldType> => {
                        if (fieldType.type === "Effectiveness") {
                            return Yup.object({
                                type: Yup.string()
                                    .oneOf(["Effectiveness"])
                                    .defined(),
                                options: Yup.object({
                                    effectiveOptionName: getOptionSchema(),
                                    partiallyEffectiveOptionName:
                                        getOptionSchema(),
                                    notEffectiveOptionName: getOptionSchema(),
                                })
                                    .defined()
                                    .test(
                                        "unique-options",
                                        t(
                                            "FieldBuilder_Error_OptionAlreadyExists"
                                        ),
                                        (value, { createError }) => {
                                            let isValid = true;
                                            handleDuplicateEffectivenessOptions(
                                                value,
                                                (key) => {
                                                    createError({
                                                        path: key,
                                                        message: t(
                                                            "FieldBuilder_Error_OptionAlreadyExists"
                                                        ),
                                                    });
                                                    isValid = false;
                                                }
                                            );
                                            return isValid;
                                        }
                                    ),
                            });
                        }
                        return getCommonFieldTypeSchema(fieldType);
                    }
                ),
                ...getCommonFieldSchemaProperties(),
            }).defined()
        )
        .defined()
        .test(getFieldsTest(t));
}

export function getSampleConclusionFieldSchema(
    t: TFunction
): Yup.Schema<Field<ConclusionFieldType, string>[]> {
    return parallelArray()
        .of(
            Yup.object({
                fieldType: Yup.lazy(
                    (
                        fieldType: ConclusionFieldType
                    ): Yup.ISchema<ConclusionFieldType> => {
                        if (fieldType.type === "Effectiveness") {
                            throw new Error(
                                "Effectiveness is not a valid field type for sample conclusions"
                            );
                        }
                        return getCommonFieldTypeSchema(fieldType);
                    }
                ),
                ...getCommonFieldSchemaProperties(),
            }).defined()
        )
        .defined()
        .test(getFieldsTest(t));
}

export function createNewControlConclusionField(
    fieldTypeName: ControlConclusionFieldTypeName,
    t: TFunction,
    fieldType?: ControlConclusionFieldType
): ControlConclusionField {
    if (
        fieldTypeName === "Effectiveness" ||
        fieldType?.type === "Effectiveness"
    ) {
        throw new Error(
            "It should not be possible to create an Effectiveness field"
        );
    }
    const newFieldType = getCommonFieldTypeDefaultValue(
        fieldTypeName,
        fieldType
    );
    const newField = {
        ...getCommonFieldProperties(),
        title: getConclusionFieldTypeInfo(t)(fieldTypeName).label,
        fieldType: newFieldType,
    };
    return newField;
}

export function createNewSampleConclusionField(
    fieldTypeName: ConclusionFieldTypeName,
    t: TFunction,
    fieldType?: ConclusionFieldType
): SampleConclusionField {
    if (
        fieldTypeName === "Effectiveness" ||
        fieldType?.type === "Effectiveness"
    ) {
        throw new Error(
            "It should not be possible to create an Effectiveness field"
        );
    }
    const newFieldType = getCommonFieldTypeDefaultValue(
        fieldTypeName,
        fieldType
    );
    const newField = {
        ...getCommonFieldProperties(),
        title: getConclusionFieldTypeInfo(t)(fieldTypeName).label,
        fieldType: newFieldType,
    };
    return newField;
}
